
import { Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { Service } from "@/types/service";

interface ServiceInfoProps {
  service: Service;
}

export const ServiceInfo = ({ service }: ServiceInfoProps) => {
  const isMobile = useIsMobile();
  
  // Get the region data to display the correct currency
  const { data: regionData } = useQuery({
    queryKey: ["region", service.region],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regions")
        .select("currency")
        .eq("code", service.region)
        .single();
      
      if (error) {
        console.error("Error fetching region data:", error);
        return { currency: "Ksh" }; // Default to Ksh if error
      }
      
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const currency = regionData?.currency || "Ksh";

  return (
    <div className="h-full flex flex-col">
      <div className="bg-gradient-to-r from-white to-primary-light rounded-lg p-5 md:p-6">
        <div className="inline-block px-2 py-0.5 mb-2.5 text-xs font-medium text-primary-dark bg-primary-light rounded-full shadow-sm">
          {(() => {
            const parentLabel = service.subcategory_details?.parent?.label;
            const subcategoryLabel = service.subcategory_details?.label;
            if (parentLabel && subcategoryLabel) {
              return `${parentLabel} - ${subcategoryLabel}`;
            }
            return subcategoryLabel || parentLabel || 'Uncategorized';
          })()}
        </div>
        <h1 className="text-xl md:text-2xl font-bold mb-3">{service.name}</h1>
        <div className="flex items-center justify-between mb-4">
          {service.price !== null && (
            <span className="text-lg md:text-xl font-bold text-primary">
              {currency} {service.price}
              <span className="text-sm font-normal text-gray-500 ml-1">
                /{service.unit || "hr"}
              </span>
            </span>
          )}
          {service.rating !== null && (
            <div className="flex items-center gap-1 bg-yellow-50 px-2.5 py-1 rounded-full">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-sm font-semibold">{service.rating.toFixed(1)}</span>
            </div>
          )}
        </div>
        
        {/* Display additional person price if available */}
        {service.additional_person !== null && service.additional_person > 0 && (
          <div className="mb-3 text-sm text-gray-700">
            <span className="font-medium">{currency} {service.additional_person}</span>
            <span className="ml-1">for each additional person</span>
          </div>
        )}
        
        {service.description ? (
          <div 
            className="prose prose-sm md:prose max-w-none text-gray-700 mb-4"
            dangerouslySetInnerHTML={{ __html: service.description }}
          />
        ) : (
          <p className="text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
            No description available for this service.
          </p>
        )}
      </div>
    </div>
  );
};
